import {
  Button,
  Divider,
  Flex,
  Icon,
  Stack,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import {
  BoxInIcon,
  CloseSIcon,
  DeleteIcon,
  DownloadIcon,
} from '@tonic-one/react-icons';
import { useToggle } from '@tonic-one/react-hooks';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import IconButton from '../../../components/IconButton';
import { PermissionNameEnum } from '@Types/permissions';
import { useGlobalSettingStore } from '../../../models/useGlobalSettingModel';
import { useOrgStore } from '../../../models/useOrgModel';
import { usePermissionStore } from '../../../models/usePermissionModel';
import { QuarantineLogTypeEnum } from '../../../types/log';
import { withEditPermission } from '../../../utils/common';
import EnableEmailPreviewModal from '../EnableEmailPreviewModal';
import {
  DeleteModal,
  DownloadModal,
  ExChangeOnlineQuarantineSettingModal,
  GmailQuarantineSettingModal,
  OneDriveQuarantineSettingModal,
  RestoreModal,
} from '../Modals';
import {
  QuarantineItemModalName,
  QuarantineItemModalState,
  QuarantineSettingModalName,
  QuarantineSettingModalState,
} from '../Modals/interface';
import { checkQuarantineItemActionEnabled } from '../utils/fns';
import { AutoDeleteDaysSetting } from './autoDeleteSetting';
import { RestoreAll } from './restoreAll';
import config from '../../../config';
import { QuarantineManageColumn } from './manageColumn';

const INLINE_TYPES = [
  QuarantineLogTypeEnum.GmailInline,
  QuarantineLogTypeEnum.ExchangeInline,
];
const EXCHANGE_TYPES = [
  QuarantineLogTypeEnum.ExchangeInline,
  QuarantineLogTypeEnum.Exchange,
  QuarantineLogTypeEnum.GmailInline,
  QuarantineLogTypeEnum.Gmail,
];
const SALESFORCE_TYPES = [
  QuarantineLogTypeEnum.SalesforceProduction,
  QuarantineLogTypeEnum.SalesforceSandbox,
];

export function QuarantineActions({
  total,
  quarantineType,
  quarantineItemsModalState,
  setQuarantineItemsModalState,
  onClear,
  hasMixedSelection,
  setTrigger,
  toggleAllRowsSelected,
  allRowChecked,
  columnOrder,
  columnNames,
  columnHideIds,
  onColumnOrderChange,
  onColumnHideIdsChange,
}: {
  total: number;
  quarantineType: QuarantineLogTypeEnum;
  quarantineItemsModalState: QuarantineItemModalState;
  setQuarantineItemsModalState: (state: QuarantineItemModalState) => void;
  onClear: () => void;
  hasMixedSelection: boolean;
  setTrigger: () => void;
  toggleAllRowsSelected: (value?: boolean) => void;
  allRowChecked: boolean;
  columnOrder: string[];
  columnNames: { [columnId: string]: string };
  columnHideIds: string[];
  onColumnOrderChange: (order: string[]) => void;
  onColumnHideIdsChange: (hideIds: string[]) => void;
}) {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const { orgId } = useOrgStore();
  const { permissions } = usePermissionStore();
  const { selectedOrgGlobalSetting } = useGlobalSettingStore();
  const [isOpen, toggleOpen] = useToggle(false);
  const [quarantineSettingModalState, setQuarantineSettingModalState] =
    useState<QuarantineSettingModalState>({
      name: QuarantineSettingModalName.None,
      todo: null,
    });
  const [selectAllForRestore, setSelectAllForRestore] = useState(false);

  const length = quarantineItemsModalState.quarantineItems.length;

  const isSmexQuarantineDisable =
    quarantineType === QuarantineLogTypeEnum.ExchangeServer &&
    !selectedOrgGlobalSetting?.smexQuarantineManagementEnabled;

  const isQuarantineEditable = withEditPermission(
    PermissionNameEnum.quarantine,
    permissions
  );

  let isRestoreBtnEnable = true;
  let isDownloadBtnEnable = true;
  let isDeleteBtnEnable = true;

  if (length === 0 || length > 100) {
    isRestoreBtnEnable = false;
    isDownloadBtnEnable = false;
    isDeleteBtnEnable = false;
  } else if (length === 1) {
    const { quarantineTypeKey, fileStatusKey } =
      quarantineItemsModalState.quarantineItems[0];
    const enable = checkQuarantineItemActionEnabled(
      quarantineTypeKey,
      fileStatusKey
    );
    isRestoreBtnEnable = enable;
    isDownloadBtnEnable = enable;
    isDeleteBtnEnable = enable;
  } else {
    if (SALESFORCE_TYPES.includes(quarantineType)) {
      isDownloadBtnEnable = false;
    }
    if (
      length > 10 ||
      ![
        QuarantineLogTypeEnum.SharePoint,
        QuarantineLogTypeEnum.OneDrive,
        QuarantineLogTypeEnum.Exchange,
        QuarantineLogTypeEnum.Teams,
      ].includes(quarantineType)
    ) {
      isDownloadBtnEnable = false;
    }

    for (const item of quarantineItemsModalState.quarantineItems) {
      const { quarantineTypeKey, fileStatusKey } = item;
      const enable = checkQuarantineItemActionEnabled(
        quarantineTypeKey,
        fileStatusKey
      );

      if (!enable) {
        isRestoreBtnEnable = false;
        isDeleteBtnEnable = false;
        break;
      }
    }
  }

  //如果是restore状态，restore按钮更新为enable
  if (selectAllForRestore) {
    isRestoreBtnEnable = true;
  }

  // disable actions while the selection mixes legacy and spodtm delegator sources
  if (hasMixedSelection) {
    isRestoreBtnEnable = false;
    isDownloadBtnEnable = false;
    isDeleteBtnEnable = false;
  }

  const isDeleteBtnShow = !INLINE_TYPES.includes(quarantineType);
  const selectToDoActionTipKey = isDeleteBtnShow
    ? 'quarantine.tips.selectToDoAction'
    : 'quarantine.tips.selectToDoActionInline';

  const isEmailPreviewBtnShow =
    isQuarantineEditable && EXCHANGE_TYPES.includes(quarantineType);

  const isExOnlineSettingBtnShow =
    isQuarantineEditable &&
    quarantineType === QuarantineLogTypeEnum.Exchange &&
    selectedOrgGlobalSetting?.exOnlineQuarantineSettingFeatureShow &&
    // TODO: the feature about exchange Online Quarantine Setting
    !config.ui.hideFeature.quarantine.exQtSetting;

  const isODSettingBtnShow =
    quarantineType === QuarantineLogTypeEnum.OneDrive &&
    selectedOrgGlobalSetting?.odQuarantineSettingFeatureShow;

  const isGmailSettingBtnShow =
    isQuarantineEditable &&
    quarantineType === QuarantineLogTypeEnum.Gmail &&
    orgId !== 'all';
  return (
    <>
      <Flex
        px="4x"
        py="3x"
        width="100%"
        align="center"
        backgroundColor={colorStyle.background.secondary}
        borderBottom="1px solid"
        borderColor={colorStyle.background.tertiary}
      >
        <Flex align="center" gap="8px">
          {length > 0 ? (
            <>
              {/* restore button */}
              <Button
                disabled={
                  !isRestoreBtnEnable ||
                  !isQuarantineEditable ||
                  isSmexQuarantineDisable
                }
                variant="secondary"
                size="md"
                onClick={() => {
                  setQuarantineItemsModalState({
                    ...quarantineItemsModalState,
                    name: QuarantineItemModalName.Restore,
                  });
                }}
              >
                <Icon as={BoxInIcon} />
                {t('common.button.restore')}
              </Button>

              {/* download button */}
              <Button
                disabled={
                  !isDownloadBtnEnable ||
                  !isQuarantineEditable ||
                  quarantineType === QuarantineLogTypeEnum.ExchangeServer
                }
                variant="secondary"
                size="md"
                onClick={() => {
                  setQuarantineItemsModalState({
                    ...quarantineItemsModalState,
                    name: QuarantineItemModalName.Download,
                  });
                }}
              >
                <Icon as={DownloadIcon} />
                {t('common.action.label.download')}
              </Button>

              {/* delete button */}
              {isDeleteBtnShow && (
                <Button
                  disabled={
                    !isDeleteBtnEnable ||
                    !isQuarantineEditable ||
                    isSmexQuarantineDisable ||
                    selectAllForRestore
                  }
                  variant="secondary"
                  size="md"
                  onClick={() => {
                    setQuarantineItemsModalState({
                      ...quarantineItemsModalState,
                      name: QuarantineItemModalName.Delete,
                    });
                  }}
                >
                  <Icon as={DeleteIcon} />
                  {t('common.button.delete')}
                </Button>
              )}
            </>
          ) : (
            <Text>{t(selectToDoActionTipKey)}</Text>
          )}
          {isEmailPreviewBtnShow && (
            <Button variant="secondary" size="md" onClick={toggleOpen}>
              {t('quarantine.modal.enableEmailPreview.title')}
            </Button>
          )}
          {length > 0 && (
            <>
              <IconButton onClick={onClear}>
                <Icon as={CloseSIcon}></Icon>
              </IconButton>
              <Text color={colorStyle.color.tertiary}>
                {t('quarantine.tips.itemsSelected', { NUMBER: length })}
              </Text>
            </>
          )}
          <>
            {/* exchange online setting button */}
            {isExOnlineSettingBtnShow && (
              <Button
                display="none" //TODO
                variant="secondary"
                size="md"
                onClick={() => {
                  setQuarantineSettingModalState({
                    name: QuarantineSettingModalName.ExchangeOnline,
                    todo: null,
                  });
                }}
              >
                {t('common.action.label.settings')}
              </Button>
            )}
            {/* oneDrive setting button */}
            {isODSettingBtnShow && (
              <Button
                display="none" //TODO
                variant="secondary"
                size="md"
                onClick={() => {
                  setQuarantineSettingModalState({
                    name: QuarantineSettingModalName.OneDrive,
                    todo: null,
                  });
                }}
              >
                {t('common.action.label.settings')}
              </Button>
            )}
            {/* gmail setting button */}
            {isGmailSettingBtnShow && (
              <Button
                variant="secondary"
                size="md"
                onClick={() => {
                  setQuarantineSettingModalState({
                    name: QuarantineSettingModalName.Gmail,
                    todo: null,
                  });
                }}
              >
                {t('common.action.label.settings')}
              </Button>
            )}
          </>
        </Flex>
        <Stack direction="row" ml="auto" gap="2x">
          <AutoDeleteDaysSetting quarantineType={quarantineType} />
          <Divider orientation="vertical" />
          <QuarantineManageColumn
            columnOrder={columnOrder}
            columnNames={columnNames}
            columnHideIds={columnHideIds}
            onColumnOrderChange={onColumnOrderChange}
            onColumnHideIdsChange={onColumnHideIdsChange}
          />
        </Stack>
      </Flex>
      <RestoreAll
        type={quarantineType}
        total={total}
        allRowChecked={allRowChecked}
        selected={quarantineItemsModalState.quarantineItems.length}
        onClear={() => {
          toggleAllRowsSelected(false);
        }}
        onSelectAll={() => {
          toggleAllRowsSelected(true);
        }}
        selectAllForRestore={selectAllForRestore}
        setSelectAllForRestore={setSelectAllForRestore}
      />
      <EnableEmailPreviewModal isOpen={isOpen} onClose={toggleOpen} />

      {isExOnlineSettingBtnShow && (
        <ExChangeOnlineQuarantineSettingModal
          modal={quarantineSettingModalState}
          setModal={setQuarantineSettingModalState}
        />
      )}

      {quarantineSettingModalState.name ===
        QuarantineSettingModalName.Gmail && (
        <GmailQuarantineSettingModal
          modal={quarantineSettingModalState}
          setModal={setQuarantineSettingModalState}
        />
      )}
      {isODSettingBtnShow && (
        <OneDriveQuarantineSettingModal
          modal={quarantineSettingModalState}
          setModal={setQuarantineSettingModalState}
        />
      )}
      <DeleteModal
        quarantineType={quarantineType}
        modal={quarantineItemsModalState}
        setModal={setQuarantineItemsModalState}
        setTrigger={setTrigger}
      />
      <RestoreModal
        quarantineType={quarantineType}
        modal={quarantineItemsModalState}
        setModal={setQuarantineItemsModalState}
        setTrigger={() => {
          setTrigger();
          setSelectAllForRestore(false);
        }}
        selectAllForRestore={selectAllForRestore}
        total={total}
      />
      <DownloadModal
        key={new Date().toISOString()}
        quarantineType={quarantineType}
        modal={quarantineItemsModalState}
        setModal={setQuarantineItemsModalState}
      />
    </>
  );
}
